import './MultiDocumentModal.scss';
import { useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import {
  MULTI_DOCUMENT_NOTE_REQUIRED_MESSAGE,
  OnboardingMultiDocument,
  OracleOnboardingMultiDocumentSectionKey
} from '@blisspointmedia/bpm-types/dist/ClientOracleOnboarding';
import { Button } from '@Components/Button';
import { Appearance, Design } from '@Components/designConstants';
import { MultiDocumentFormValues, MultiDocumentModalCopy } from '../multi-document-constants';
import { validateMultiDocumentForm } from '../multi-document-utils';
import { SelectedFileChip, SourceFilePicker } from './SourceFilePicker';

interface MultiDocumentModalProps {
  sectionKey: OracleOnboardingMultiDocumentSectionKey;
  copy: MultiDocumentModalCopy;
  // Present in edit mode.
  doc?: OnboardingMultiDocument;
  onHide: () => void;
  // A rejection renders inline, and the modal stays open.
  onSubmit: (values: MultiDocumentFormValues) => Promise<void>;
  isSubmitting: boolean;
}

export const MultiDocumentModal = ({
  sectionKey,
  copy,
  doc,
  onHide,
  onSubmit,
  isSubmitting
}: MultiDocumentModalProps) => {
  const isEdit = doc !== undefined;
  const [file, setFile] = useState<File | null>(null);
  const [isReplacing, setIsReplacing] = useState(false);
  const [note, setNote] = useState(doc?.note ?? '');
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    // Null on an add with no file picked.
    const trimmedNote = note.trim();
    const values: MultiDocumentFormValues | null = doc
      ? { doc, file, note: trimmedNote }
      : file && { file, note: trimmedNote };
    const message = validateMultiDocumentForm({
      hasFile: values !== null && (!isReplacing || file !== null),
      note,
      noteRequiredMessage: MULTI_DOCUMENT_NOTE_REQUIRED_MESSAGE[sectionKey]
    });
    if (message || values === null) {
      setError(message);
      return;
    }
    setError(null);
    try {
      await onSubmit(values);
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <Modal show onHide={() => !isSubmitting && onHide()} className='multi-document-modal' centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {isEdit ? 'Edit' : 'Add'} {copy.itemTitle}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className='multi-document-modal-body'>
        <section className='multi-document-field'>
          <div className='multi-document-field-label'>The document</div>
          {doc && !isReplacing ? (
            <SelectedFileChip
              name={doc.sourceFileName}
              caption='Current file'
              action={
                <Button appearance={Appearance.EMPTY} onClick={() => setIsReplacing(true)}>
                  Replace file
                </Button>
              }
            />
          ) : (
            <SourceFilePicker
              selectedFile={file}
              onFile={picked => {
                setError(null);
                setFile(picked);
              }}
              onError={message => {
                setFile(null);
                setError(message);
              }}
            />
          )}
        </section>
        <section className='multi-document-field'>
          <div className='multi-document-field-label'>
            {copy.noteLabel}
            <span className='multi-document-required'>*</span>
          </div>
          <div className='multi-document-field-help'>{copy.noteHelp}</div>
          <Form.Control
            as='textarea'
            rows={5}
            value={note}
            aria-label={copy.noteLabel}
            onChange={e => setNote(e.target.value)}
          />
        </section>
        {error && <div className='validation-error'>{error}</div>}
      </Modal.Body>
      <Modal.Footer className='onboarding-modal-footer'>
        <Button
          appearance={Appearance.OUTLINED}
          design={Design.NEUTRAL}
          disabled={isSubmitting}
          onClick={onHide}
        >
          Cancel
        </Button>
        <Button loading={isSubmitting} onClick={submit}>
          {isEdit ? 'Save' : copy.addLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
